import { createFileRoute } from "@tanstack/react-router";
import { useCoverageMatrix } from "@/api/hooks";
import { CoverageMatrix } from "@/components/coverage/CoverageMatrix";
import { CoverageGapsIndex } from "@/components/coverage/CoverageGapsIndex";
import { BackLink, Breadcrumb } from "@/components/shared";

interface CoverageSearch {
  entity?: string;
}

export const Route = createFileRoute("/coverage")({
  component: CoveragePage,
  validateSearch(raw: Record<string, unknown>): CoverageSearch {
    return { entity: typeof raw.entity === "string" ? raw.entity : undefined };
  },
});

function CoveragePage() {
  const { entity } = Route.useSearch();
  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
      {/* No entity selected → the live gaps index (pick one to drill in).
          With an entity → that entity's coverage matrix. */}
      {entity ? <CoverageMatrixPanel entity={entity} /> : <CoverageGapsIndex />}
    </div>
  );
}

function CoverageMatrixPanel({ entity }: { entity: string }) {
  const { data, isLoading, isError } = useCoverageMatrix(entity);

  // The matrix is meaningful only with at least one decade row. An empty
  // response means the entity resolved to no surveyed coverage (or isn't in
  // the corpus); rather than show a mismatched demo fixture, name the gap
  // honestly. Live coverage is wired for every entity, so there's no fixture.
  const hasLive = !!data && data.by_institution.rows.length > 0;

  if (isLoading && !data) return <CoverageSkeleton />;
  if (hasLive) return <CoverageMatrix data={data} fallbackName={entity} />;
  return <CoverageEmpty entity={entity} isError={isError} />;
}

function CoverageSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-live="polite" aria-busy="true">
      <div className="h-7 w-1/3 animate-pulse rounded bg-rule/30" />
      <div className="h-4 w-1/4 animate-pulse rounded bg-rule/20" />
      <div className="mt-4 h-[50vh] animate-pulse rounded-container bg-rule/20" />
      <span className="sr-only">Loading coverage matrix…</span>
    </div>
  );
}

/** Honest empty/error state — never a stand-in fixture for the wrong entity. */
function CoverageEmpty({ entity, isError }: { entity: string; isError: boolean }) {
  return (
    <section className="qm-fade-in mx-auto flex max-w-3xl flex-col gap-6">
      <Breadcrumb
        segments={[{ label: "Corpus", to: "/", search: { mode: "query" } }, { label: "Coverage" }]}
      />
      <div className="py-12 text-center">
        <p className="text-caps uppercase tracking-caps text-ink-tertiary">
          {isError ? "Unavailable" : "No coverage"}
        </p>
        <h1 className="mt-3 font-serif text-display leading-tight text-ink-primary">
          {isError ? "Coverage couldn’t be loaded" : `No coverage recorded for ${entity}`}
        </h1>
        <p className="mx-auto mt-3 max-w-md font-serif text-lede italic text-ink-secondary">
          {isError
            ? "The coverage service didn’t respond. Try again in a moment."
            : "The surveyed corpus holds no dated documents for this subject to chart across time."}
        </p>
        <BackLink
          fallback={{ to: "/", search: { mode: "query" } }}
          className="mt-6 inline-block text-meta text-ink-info hover:text-ink-primary"
        >
          ← Go back
        </BackLink>
      </div>
    </section>
  );
}
